import { Button, FlyoutButton, Icon, Spinner } from '@acpaas-ui/react-components';
import _, { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { userToString } from '../../../lib/auth.utils';
import { comments } from '../../../services/bff.api';
import { INPUT_STATES } from '../../forms/forms.utils';
import TextField from '../../forms/TextField';

const CommentForm = ({
  text,
  data,
  disabled,
  submitting,
  editMode,
  borderless,
  onEditCancel,
  onEditSubmit,
  onCommentAdd,
  onInput
}) => {
  const params = useParams();
  const searchInputRef = useRef(null);
  const mentionSearchId = useRef(uuid()).current;
  const inputCommentRef = useRef(null);
  const [shouldShowPlaceholder, setShouldShowPlaceholder] = useState(true);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const addComment = () => {
    onCommentAdd(() => {
      const node = inputCommentRef.current || {};
      node.innerHTML = '';
    });
  };

  const hidePlaceholder = () => {
    setShouldShowPlaceholder(false);
  };
  const showPlaceholder = () => {
    setShouldShowPlaceholder(true);
  };

  const toggleMentionSearch = () => {
    document.getElementById(`comment-form-mention-search_${mentionSearchId}`)?.click();
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleInputMention = (e) => {
    if (e.key === '@') {
      e.preventDefault();
      e.stopPropagation();
      toggleMentionSearch();
    }
  };

  const handleUsersSearch = async (value) => {
    setIsFetchingUsers(true);
    const { json } = await comments.searchMentionUsers(params.id, value);

    setIsFetchingUsers(false);
    setUserSearchResults(
      json?._embedded?.users?.map((c) => ({
        id: c.id,
        name: userToString(c?.spoContactData),
        email: c?.spoContactData?.email ?? ''
      })) ?? []
    );
  };

  const inputComment = () => {
    const text = inputCommentRef.current?.innerText ?? '';
    onInput(text, 'text');
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInput = useCallback(_.debounce(inputComment, 250, { leading: true }), []);

  const parseMentions = useCallback(() => {
    let parsed = text;
    _.sortBy(_.get(data, 'mentions', []), 'startIndex').forEach((mention) => {
      parsed = parsed.replace(`@${mention.name}`, `<span class="mention">@${mention.name}</span>`);
    });

    return parsed;
  }, [text, data]);

  const handleMentionAdd = (user) => {
    inputCommentRef.current.innerHTML = `<span>${parseMentions()}</span>`;
    const mentionStartIndex = inputCommentRef.current.innerText.length;
    inputCommentRef.current.innerHTML = `${inputCommentRef.current.innerHTML}<span class="mention">@${user.name}</span>`;
    const mentionEndIndex = inputCommentRef.current.innerText.length - 1;
    inputCommentRef.current.innerHTML = `${inputCommentRef.current.innerHTML}<span>&#8203;</span>`;
    const updatedMentions = _.uniqBy(
      [
        ...(data.mentions || []),
        { ...user, emailSent: false, startIndex: mentionStartIndex, endIndex: mentionEndIndex }
      ],
      'id'
    );
    onInput(updatedMentions, 'mentions');

    var range = document.createRange();
    var sel = window.getSelection();
    const lastNode = inputCommentRef.current.childNodes[inputCommentRef.current.childNodes.length - 1];
    range.setStart(lastNode, 1);
    sel.removeAllRanges();
    sel.addRange(range);

    inputComment();
  };

  const debouncedSearch = useCallback(debounce(handleUsersSearch, 350), []);

  const handleSearchChange = (searchQuery) => {
    setSearchValue(searchQuery);

    if (!!searchQuery) {
      debouncedSearch(searchQuery);
    }
  };

  const handleSelect = (id) => {
    const selectedUser = userSearchResults.find((user) => user.id === id);
    handleMentionAdd(selectedUser);
    document.getElementById(`comment-form-mention-search_${mentionSearchId}`)?.click();
    setSearchValue('');
  };

  useEffect(() => {
    inputCommentRef.current.innerHTML = `<span>${parseMentions()}</span>`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {});

  return (
    <div className={`spo-m-comment-form ${editMode ? 'is-edit' : ''} ${borderless ? 'borderless' : ''}`}>
      <div
        ref={inputCommentRef}
        onInput={debouncedInput}
        onFocus={hidePlaceholder}
        onBlur={showPlaceholder}
        onKeyDown={handleInputMention}
        className="spo-m-comment-form__text a-input"
        contentEditable
      ></div>
      {shouldShowPlaceholder && !text ? (
        <span className="spo-m-comment-form__placeholder">Schrijf hier uw commentaar...</span>
      ) : null}
      <div className={`spo-m-comment-form__actions ${editMode ? 'edit' : ''}`}>
        <div className="u-margin-right-auto">
          <FlyoutButton negative id={`comment-form-mention-search_${mentionSearchId}`} icon="ai-single-neutral">
            <div>
              <li className="m-selectable-list__item search" onClick={() => searchInputRef?.current?.focus()}>
                <TextField
                  ref={searchInputRef}
                  state={INPUT_STATES.DEFAULT}
                  value={searchValue}
                  placeholder="Zoeken"
                  onChange={handleSearchChange}
                  iconRight="ai-search"
                />
              </li>
              <ul className="m-selectable-list">
                {isFetchingUsers ? (
                  <li className={`m-selectable-list__item u-text-center`} key={'fetching-results'}>
                    <Spinner />
                  </li>
                ) : userSearchResults?.length < 1 ? (
                  <li className={`m-selectable-list__item`} key={'no-results'}>
                    <span className="u-text-light">Geen resultaten</span>
                  </li>
                ) : (
                  userSearchResults.map((opt) => (
                    <li className={`m-selectable-list__item`} key={opt.id} onClick={() => handleSelect(opt.id)}>
                      <div className="single-select">{opt.name}</div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </FlyoutButton>
        </div>
        {editMode && (
          <>
            <Button
              className="spo-a-activity-item__edit-button u-margin-right-xs"
              size="small"
              onClick={onEditCancel}
              negative
            >
              Annuleer
            </Button>
            <Button
              className={submitting ? 'loading has-icon-right' : ''}
              size="small"
              disabled={disabled || submitting}
              onClick={onEditSubmit}
              iconRight={submitting ? undefined : 'ai-check-1'}
            >
              Bewaren
              {submitting ? <Spinner size="small" /> : ''}
            </Button>
          </>
        )}
        {!editMode && onCommentAdd && (
          <div
            className={`submit-icon ${text && !submitting ? 'submit-icon--is-active' : ''}`}
            onClick={!!text && !submitting ? addComment : null}
          >
            {submitting ? <Spinner /> : <Icon name="ai-send-email" />}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentForm;
